import { normalize } from "normalizr";
import { all, call, put, takeLatest } from "redux-saga/effects";

import {
    BLOGS_ALBUM_DETAIL_FETCH_TRIGGER,
    BlogsAlbumDetailFetchNormalizedResponse,
    BlogsAlbumDetailFetchTriggerAction,
    blogsAlbumDetailFetchApi,
} from "@/mk2/apps/blogs/containers/BlogsAlbumDetail/BlogsAlbumDetail.actions";
import {
    CommentsFetchNormalizedResponse,
    commentsFetchApi,
} from "@/mk2/apps/blogs/containers/Comments/Comments.actions";
import { getCommentParent } from "@/mk2/apps/blogs/helpers";
import { ComplaintEntity, ComplaintSchema } from "@/mk2/containers/Complaint/Complaint.schemas";
import { XHRAction, XHRGetError, handleXHRGetErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import { BlogPostEntity, BlogPostSchema, CommentEntity, CommentSchema } from "@/mk2/schemas";
import { HistoryAction } from "@/mk2/services/browserHistory";
import { blogs_api_album_detail_url } from "@/mk/urls/functions";

const logger = getLogger("blogs/BlogsAlbumDetail.sagas");

interface ApiResponseFetchBlogsAlbumDetail {
    body: {
        canAdd: boolean;
        post: BlogPostEntity;
        comments: CommentEntity[];
        complaints: ComplaintEntity[];
    };
}

function* fetchBlogsAlbumDetail({
    username,
    postSlug,
    accessKey,
    xhr,
    history,
    location,
    routeNavigate,
}: BlogsAlbumDetailFetchTriggerAction & XHRAction & HistoryAction) {
    yield put(blogsAlbumDetailFetchApi.request(username, postSlug, accessKey));

    try {
        const response: ApiResponseFetchBlogsAlbumDetail = yield call(() =>
            xhr.get(blogs_api_album_detail_url(), {
                username,
                postSlug,
                accessKey,
                canAppendAccessKey: "true",
            }),
        );

        const normalizedResponse: BlogsAlbumDetailFetchNormalizedResponse = normalize(
            {
                post: response.body.post,
                comments: response.body.comments,
                complaints: response.body.complaints,
            },
            {
                post: BlogPostSchema,
                comments: [CommentSchema],
                complaints: [ComplaintSchema],
            },
        );

        // integrate loaded comments into Comments container
        const commentsNormalizedResponse: CommentsFetchNormalizedResponse = {
            result: {
                comments: normalizedResponse.result.comments,
                canAdd: response.body.canAdd,
                hasHiddenText: false,
            },
            entities: normalizedResponse.entities,
        };
        yield put(
            commentsFetchApi.success(
                getCommentParent(response.body.post),
                commentsNormalizedResponse,
            ),
        );

        yield put(
            blogsAlbumDetailFetchApi.success(username, postSlug, accessKey, normalizedResponse),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(
            blogsAlbumDetailFetchApi.failure(username, postSlug, accessKey, normalizeError(error)),
        );
    }
}

export default function* root() {
    yield all([takeLatest(BLOGS_ALBUM_DETAIL_FETCH_TRIGGER, fetchBlogsAlbumDetail)]);
}
